import React, { useState, useEffect } from "react";
import "./BoatStatus.css";
import { getAGEToken } from "../../utils/authenticate";
import { AppConfig } from "../../AppConfig";
import NfsButton from "../genericComponents/NfsButton/NfsButton";
import CloseIcon from "../../assets/close.svg?react";

const BOATS_STORAGE_KEY = "nfs-boats";

interface DatabaseStatus {
    databaseName: string;
    lastSyncDateTime: string;
}

interface BoatStatusData {
    boatName: string;
    databaseStatuses: DatabaseStatus[];
}

interface BoatStatusProps {
    onClose?: () => void;
}

const BoatStatus: React.FC<BoatStatusProps> = ({ onClose }) => {
    const [boats, setBoats] = useState<string[]>([]);
    const [boatStatuses, setBoatStatuses] = useState<Record<string, BoatStatusData>>({});
    const [newBoatName, setNewBoatName] = useState("");
    const [loading, setLoading] = useState<Record<string, boolean>>({});
    const addBoatDialogRef = React.useRef<HTMLDialogElement>(null);

    // Initialize boats from localStorage or use default
    useEffect(() => {
        const savedBoats = localStorage.getItem(BOATS_STORAGE_KEY);
        if (savedBoats) {
            const parsedBoats = JSON.parse(savedBoats);
            setBoats(parsedBoats);
            parsedBoats.forEach((boat: string) => fetchBoatStatus(boat));
        } else {
            setBoats(AppConfig.BoatStatusDefaultBoats);
            localStorage.setItem(BOATS_STORAGE_KEY, JSON.stringify(AppConfig.BoatStatusDefaultBoats));
            AppConfig.BoatStatusDefaultBoats.forEach((boat) => fetchBoatStatus(boat));
        }
    }, []);

    // Helper function to get database sync time
    const getDatabaseTime = (boat: BoatStatusData | undefined, dbName: string): string => {
        if (!boat || !boat.databaseStatuses) return "-";
        const db = boat.databaseStatuses.find((d) => d.databaseName.toLowerCase() === dbName.toLowerCase());
        if (!db || !db.lastSyncDateTime) return "-";
        // API incorrectly returns Norwegian local time with +00:00 offset
        // Strip the timezone offset and parse as local time
        const dateString = db.lastSyncDateTime.replace(/([+-]\d{2}:\d{2}|Z)$/, "");
        const date = new Date(dateString);
        return date.toLocaleString("nb-NO", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    // Helper function to get CSS class for date cell
    const getDateCellClass = (boat: BoatStatusData | undefined, dbName: string): string => {
        if (!boat || !boat.databaseStatuses) return "";
        const db = boat.databaseStatuses.find((d) => d.databaseName.toLowerCase() === dbName.toLowerCase());
        if (!db) return "";

        const date = new Date(db.lastSyncDateTime);
        const timestamp = date.getTime();

        // Check if it's the Unix epoch (timestamp 0 or very close to it)
        // This handles timezone differences - checking if within 24 hours of epoch
        if (timestamp < 86400000) {
            return "boat-status-date-epoch";
        }

        // Check if more than 1 day old
        const now = new Date();
        const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        if (date < oneDayAgo) {
            return "boat-status-date-old";
        }

        return "";
    };

    // Fetch status for a specific boat
    const fetchBoatStatus = async (boatName: string) => {
        setLoading((prev) => ({ ...prev, [boatName]: true }));
        try {
            const url = AppConfig.NFSAPI.BaseApiUrl + `boatclient/status/${boatName}`;
            const token = await getAGEToken();
            const response = await fetch(url, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            const data = await response.json();
            setBoatStatuses((prev) => ({ ...prev, [boatName]: data }));
        } catch (error) {
            console.error(`Error fetching status for ${boatName}:`, error);

            setBoatStatuses((prev) => ({
                ...prev,
                [boatName]: {
                    boatName,
                    databaseStatuses: [],
                },
            }));
        } finally {
            setLoading((prev) => ({ ...prev, [boatName]: false }));
        }
    };

    // Add a new boat
    const handleAddBoat = () => {
        if (newBoatName.trim() && !boats.includes(newBoatName.trim())) {
            const updatedBoats = [...boats, newBoatName.trim()];
            setBoats(updatedBoats);
            localStorage.setItem(BOATS_STORAGE_KEY, JSON.stringify(updatedBoats));
            fetchBoatStatus(newBoatName.trim());
            setNewBoatName("");
            addBoatDialogRef.current?.close();
        }
    };

    const handleCancelAddBoat = () => {
        setNewBoatName("");
        addBoatDialogRef.current?.close();
    };

    const handleOpenModal = () => {
        addBoatDialogRef.current?.showModal();
    };

    const handleRemoveBoat = (boatName: string) => {
        if (confirm(`Are you sure you want to remove ${boatName}?`)) {
            const updatedBoats = boats.filter((boat) => boat !== boatName);
            setBoats(updatedBoats);
            localStorage.setItem(BOATS_STORAGE_KEY, JSON.stringify(updatedBoats));
            setBoatStatuses((prev) => {
                const updated = { ...prev };
                delete updated[boatName];
                return updated;
            });
        }
    };

    const handleReloadDefaults = () => {
        if (confirm("Are you sure you want to reload the default boats? This will replace your current list.")) {
            setBoats(AppConfig.BoatStatusDefaultBoats);
            localStorage.setItem(BOATS_STORAGE_KEY, JSON.stringify(AppConfig.BoatStatusDefaultBoats));
            setBoatStatuses({});
            AppConfig.BoatStatusDefaultBoats.forEach((boat) => fetchBoatStatus(boat));
        }
    };

    return (
        <div className="boat-status">
            <div className="boat-status-header">
                <div>
                    <h3>Status på synkronisering av databaser på båter</h3>
                    <p>Per nå er dette basert på logger vi får fra båtene</p>
                </div>
                {onClose && (
                    <button
                        type="button"
                        className="empty-button"
                        style={{ position: "absolute", right: "16px", top: "16px" }}
                        onClick={onClose}
                    >
                        <CloseIcon />
                    </button>
                )}
            </div>
            <table>
                <thead>
                    <tr>
                        <th>Boat Name</th>
                        <th>Work</th>
                        <th>Admin</th>
                        <th>FDV</th>
                        <th>Operational</th>
                        <th>Environmental Areas</th>
                        <th>Primary</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {[...boats]
                        .sort((a, b) => a.localeCompare(b))
                        .map((boatName) => {
                            const boat = boatStatuses[boatName];
                            const isLoading = loading[boatName];
                            return (
                                <tr key={boatName}>
                                    <td>{boatName}</td>
                                    <td className={getDateCellClass(boat, "Work")}>
                                        {isLoading ? "Loading..." : getDatabaseTime(boat, "Work")}
                                    </td>
                                    <td className={getDateCellClass(boat, "Admin")}>
                                        {isLoading ? "Loading..." : getDatabaseTime(boat, "Admin")}
                                    </td>
                                    <td className={getDateCellClass(boat, "FDV")}>
                                        {isLoading ? "Loading..." : getDatabaseTime(boat, "FDV")}
                                    </td>
                                    <td className={getDateCellClass(boat, "Operational")}>
                                        {isLoading ? "Loading..." : getDatabaseTime(boat, "Operational")}
                                    </td>
                                    <td className={getDateCellClass(boat, "EnvironmentalAreas")}>
                                        {isLoading ? "Loading..." : getDatabaseTime(boat, "EnvironmentalAreas")}
                                    </td>
                                    <td className={getDateCellClass(boat, "Primary")}>
                                        {isLoading ? "Loading..." : getDatabaseTime(boat, "Primary")}
                                    </td>
                                    <td className="boat-status-action-buttons">
                                        <NfsButton
                                            onClick={() => fetchBoatStatus(boatName)}
                                            disabled={isLoading}
                                            outlined
                                            className="boat-status-action-button"
                                        >
                                            {isLoading ? "Loading..." : "Refresh"}
                                        </NfsButton>
                                        <NfsButton
                                            onClick={() => handleRemoveBoat(boatName)}
                                            outlined
                                            className="boat-status-action-button"
                                        >
                                            Remove
                                        </NfsButton>
                                    </td>
                                </tr>
                            );
                        })}
                </tbody>
            </table>
            <div className="boat-status-footer">
                <div className="boat-status-add-button-container">
                    <NfsButton onClick={handleOpenModal}>Add Boat</NfsButton>
                </div>
                <div className="boat-status-reload-button-container">
                    <NfsButton onClick={handleReloadDefaults} outlined small>
                        Reload defaults
                    </NfsButton>
                </div>
            </div>

            <dialog
                ref={addBoatDialogRef}
                className="boat-status-modal"
                onClick={(e) => {
                    if (e.target === addBoatDialogRef.current) {
                        handleCancelAddBoat();
                    }
                }}
            >
                <h3 className="boat-status-modal-title">Add Boat</h3>
                <p className="boat-status-modal-warning">
                    The boat name must be exactly the Windows PC name, including capitals
                </p>
                <input
                    type="text"
                    value={newBoatName}
                    onChange={(e) => setNewBoatName(e.target.value)}
                    placeholder="Enter boat name"
                    onKeyDown={(e) => e.key === "Enter" && handleAddBoat()}
                    className="boat-status-modal-input"
                    autoFocus
                />
                <div className="boat-status-modal-actions">
                    <NfsButton onClick={handleCancelAddBoat} outlined>
                        Cancel
                    </NfsButton>
                    <NfsButton onClick={handleAddBoat} disabled={!newBoatName.trim()}>
                        OK
                    </NfsButton>
                </div>
            </dialog>
        </div>
    );
};

export default BoatStatus;
